/**
 * The order a listing's photos are shown in.
 *
 * S3 has no notion of order: `ListObjects` returns keys sorted lexicographically,
 * so until now the first photo of a listing — the one on the card, in the
 * catalogue and in every Telegram and Viber preview — was whichever filename
 * happened to sort first. A realtor who wanted a different cover had to rename
 * files on disk and re-upload.
 *
 * So the chosen order lives on the listing itself, in `objects_v3` /
 * `archives_v2`, as a list of file NAMES (not keys — the key prefix is just the
 * listing id repeated, and names are unique inside an album). Keeping it on the
 * item rather than beside the photos matters for the catalogue: the item is
 * already fetched with the listing, so ordering the cards costs no extra
 * request, and a reorder shows publicly at once rather than when a CDN cache
 * happens to expire.
 *
 * Nothing is renamed and nothing is re-uploaded: only this list changes.
 */

/** DynamoDB attribute value for a list of names, or null when there is none. */
export function photoOrderAttribute(names) {
  if (!names || names.length === 0) return null;
  return {
    L: names.map(function (name) {
      return { S: String(name) };
    }),
  };
}

/** Read the attribute back off a raw DynamoDB item. */
export function readPhotoOrder(element) {
  if (!element || !element.photoOrder || !element.photoOrder.L) return [];
  return element.photoOrder.L.map(function (entry) {
    return entry.S;
  }).filter(Boolean);
}

/**
 * Sort photos into the saved order.
 *
 * Anything not mentioned in the order — a photo uploaded after the last
 * reorder, say — keeps its alphabetical place at the end rather than
 * disappearing or jumping to the front. A name in the order that no longer
 * exists is simply skipped, so deleting a photo never needs the list rewritten.
 */
export function applyPhotoOrder(photos, order) {
  if (!photos || photos.length === 0) return photos || [];
  if (!order || order.length === 0) return photos;

  const byName = {};
  for (let i = 0; i < photos.length; i++) byName[photos[i].name] = photos[i];

  const ordered = [];
  const taken = {};
  for (let i = 0; i < order.length; i++) {
    const photo = byName[order[i]];
    if (photo && !taken[order[i]]) {
      ordered.push(photo);
      taken[order[i]] = true;
    }
  }
  for (let i = 0; i < photos.length; i++) {
    if (!taken[photos[i].name]) ordered.push(photos[i]);
  }
  return ordered;
}

/** Move one item of an array to another index, returning a new array. */
export function moveItem(list, from, to) {
  const next = list.slice();
  if (from < 0 || from >= next.length) return next;
  const bounded = Math.max(0, Math.min(next.length - 1, to));
  const moved = next.splice(from, 1)[0];
  next.splice(bounded, 0, moved);
  return next;
}
